import { useNavigate } from 'react-router-dom';
import { Avatar, Breadcrumb, Button, Space, Tooltip, Typography, message, theme } from 'antd';
import { EditOutlined, StopOutlined, CopyOutlined } from '@ant-design/icons';
import { EstadoClienteTag } from './EstadoClienteTag';
import { TipoClienteTag } from './TipoClienteTag';
import { useT } from '../../../locales';
import { getInitials } from '../../../lib/initials';
import type { ClienteResponse } from '@aid/shared';

const { Text } = Typography;

interface ClienteDetailHeaderProps {
  cliente: ClienteResponse;
  canUpdate: boolean;
  canDeactivate: boolean;
  onEdit: () => void;
  onDeactivate: () => void;
}

export function ClienteDetailHeader({
  cliente,
  canUpdate,
  canDeactivate,
  onEdit,
  onDeactivate,
}: ClienteDetailHeaderProps) {
  const t = useT();
  const navigate = useNavigate();
  const { token } = theme.useToken();

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          marginBottom: 24,
        }}
      >
        <Breadcrumb
          items={[
            { title: <a onClick={() => navigate('/clientes')}>{t('pages.clientes.title')}</a> },
            {
              title: cliente.nombre ?? `${t('pages.clientes.detail')} #${cliente.id}`,
            },
          ]}
        />
        <Space size={8} style={{ flexShrink: 0 }}>
          {canUpdate && (
            <Button icon={<EditOutlined />} onClick={onEdit}>
              {t('common.edit')}
            </Button>
          )}
          {canDeactivate && (
            <Button danger icon={<StopOutlined />} onClick={onDeactivate}>
              {t('pages.clientes.deactivation.open')}
            </Button>
          )}
        </Space>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          paddingBottom: 20,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Avatar
          size={64}
          style={{
            backgroundColor: token.colorPrimaryBg,
            color: token.colorPrimary,
            fontSize: 22,
            fontWeight: 600,
            flexShrink: 0,
          }}
          aria-hidden
        >
          {getInitials(cliente.nombre)}
        </Avatar>
        <div style={{ minWidth: 0 }}>
          <Typography.Title
            level={3}
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 600,
              letterSpacing: '-0.015em',
              lineHeight: 1.2,
            }}
          >
            {cliente.nombre ?? `${t('pages.clientes.detail')} #${cliente.id}`}
          </Typography.Title>
          <Space size={8} align="center" style={{ marginTop: 8, flexWrap: 'wrap' }}>
            <EstadoClienteTag estado={cliente.estadoCliente} />
            <TipoClienteTag tipo={cliente.tipoCliente} />
            <Text type="secondary" style={{ fontSize: 13 }}>
              {/* El campo legacy ip guarda el CÓDIGO del cliente (AID9.x);
                  el id numérico es interno y solo se muestra como fallback. */}
              {cliente.ip
                ? `${t('pages.clientes.codigo')}: ${cliente.ip}`
                : `${t('common.id')}: ${cliente.id}`}
            </Text>
            {cliente.ip && (
              <Tooltip title={t('pages.clientes.copiarCodigo')}>
                <Button
                  type="text"
                  size="small"
                  aria-label={t('pages.clientes.copiarCodigo')}
                  icon={<CopyOutlined />}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(cliente.ip!);
                      message.success(t('pages.clientes.codigoCopiado'));
                    } catch {
                      message.error(t('pages.clientes.copiarError'));
                    }
                  }}
                />
              </Tooltip>
            )}
          </Space>
        </div>
      </div>
    </div>
  );
}
